import React, { useRef } from 'react';
import { Button } from '../ui/button';
import { prepareMediaHtml, serializeMediaHtml, hydrateMedia } from './protectedMedia';
import {
  Bold, Italic, Underline, Heading1, Heading2, List, ListOrdered, CheckSquare,
  Table as TableIcon, Image as ImageIcon, Video, FileText, Link2, Info, AlertTriangle,
  CheckCircle2, ChevronRight, Code, Upload
} from 'lucide-react';

const exec = (command, value = null) => document.execCommand(command, false, value);

const ToolButton = ({ title, onClick, children, testid }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="h-8 w-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
    data-testid={testid}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-5 bg-slate-200 mx-1" />;

/** Dependency-free rich text editor (headings, tables, checklists, media, callouts, expanders). */
export const RichTextEditor = ({ value, onChange, pages = [], placeholder = 'Start writing...', onUploadFile, fullHeight = false, onUploadStateChange }) => {
  const ref = useRef(null);
  const fileInput = useRef(null);
  const mediaUrls = useRef(new Map());
  React.useEffect(() => () => { mediaUrls.current.forEach(URL.revokeObjectURL); }, []);
  const [uploading, setUploading] = React.useState(false);

  // Apply external edits (load/AI/undo) without replacing the DOM on each keystroke.
  React.useLayoutEffect(() => {
    if (ref.current && serializeMediaHtml(ref.current) !== (value || '')) ref.current.innerHTML = prepareMediaHtml(value || '');
    if (ref.current) hydrateMedia(ref.current, mediaUrls.current);
  }, [value]);

  const sync = () => onChange(serializeMediaHtml(ref.current));

  const insertHtml = (html) => {
    ref.current?.focus();
    exec('insertHTML', prepareMediaHtml(html));
    if (ref.current) hydrateMedia(ref.current, mediaUrls.current);
    sync();
  };

  const htmlForUpload = (uploaded) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${uploaded.url}`;
    if (uploaded.is_image) return `<img src="${url}" alt="${uploaded.filename}" class="kb-image" />`;
    if (uploaded.is_pdf) return `<div class="kb-embed"><iframe src="${url}" title="${uploaded.filename}"></iframe></div><p><br/></p>`;
    if ((uploaded.content_type || '').startsWith('video/')) return `<div class="kb-embed"><video src="${url}" controls></video></div><p><br/></p>`;
    return `<p><a class="kb-page-link" href="${url}" target="_blank" rel="noreferrer">${uploaded.filename}</a></p>`;
  };

  const uploadFiles = async (files) => {
    if (!onUploadFile || !files?.length) return;
    setUploading(true);
    onUploadStateChange?.(true);
    try {
      for (const file of Array.from(files)) {
        const uploaded = await onUploadFile(file);
        if (uploaded) insertHtml(htmlForUpload(uploaded));
      }
    } finally {
      setUploading(false);
      onUploadStateChange?.(false);
    }
  };

  const insertLinkToPage = () => {
    if (!pages.length) return;
    const list = pages.slice(0, 30).map((p, i) => `${i + 1}. ${p.title}`).join('\n');
    const choice = window.prompt(`Link to which page?\n\n${list}\n\nEnter the number:`);
    const page = pages[parseInt(choice, 10) - 1];
    if (page) insertHtml(`<a class="kb-page-link" href="/officium-pages?page=${encodeURIComponent(page.id)}" data-page-id="${page.id}">${page.title.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</a>&nbsp;`);
  };

  return (
    <div className={fullHeight ? 'min-h-0 flex-1 flex flex-col border-t border-slate-200 overflow-hidden' : 'border border-slate-200 rounded-xl overflow-hidden'} data-testid="kb-editor">
      <div className="shrink-0 flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50" data-testid="kb-editor-toolbar">
        <ToolButton title="Heading 1" onClick={() => { exec('formatBlock', '<h1>'); sync(); }} testid="kb-h1"><Heading1 className="h-4 w-4" /></ToolButton>
        <ToolButton title="Heading 2" onClick={() => { exec('formatBlock', '<h2>'); sync(); }} testid="kb-h2"><Heading2 className="h-4 w-4" /></ToolButton>
        <Divider />
        <ToolButton title="Bold" onClick={() => { exec('bold'); sync(); }} testid="kb-bold"><Bold className="h-4 w-4" /></ToolButton>
        <ToolButton title="Italic" onClick={() => { exec('italic'); sync(); }} testid="kb-italic"><Italic className="h-4 w-4" /></ToolButton>
        <ToolButton title="Underline" onClick={() => { exec('underline'); sync(); }} testid="kb-underline"><Underline className="h-4 w-4" /></ToolButton>
        <Divider />
        <ToolButton title="Bullet list" onClick={() => { exec('insertUnorderedList'); sync(); }} testid="kb-ul"><List className="h-4 w-4" /></ToolButton>
        <ToolButton title="Numbered list" onClick={() => { exec('insertOrderedList'); sync(); }} testid="kb-ol"><ListOrdered className="h-4 w-4" /></ToolButton>
        <ToolButton title="Checklist" onClick={() => insertHtml('<ul class="kb-checklist"><li>Task</li></ul>')} testid="kb-checklist"><CheckSquare className="h-4 w-4" /></ToolButton>
        <Divider />
        <ToolButton
          title="Table"
          onClick={() => {
            const cols = parseInt(window.prompt('Columns?', '3'), 10) || 3;
            const rows = parseInt(window.prompt('Rows (excluding header)?', '2'), 10) || 2;
            const header = `<tr>${Array.from({ length: cols }, (_, i) => `<th>Column ${i + 1}</th>`).join('')}</tr>`;
            const body = Array.from({ length: rows }, () => `<tr>${'<td>&nbsp;</td>'.repeat(cols)}</tr>`).join('');
            insertHtml(`<table class="kb-table"><tbody>${header}${body}</tbody></table><p><br/></p>`);
          }}
          testid="kb-table"
        ><TableIcon className="h-4 w-4" /></ToolButton>
        <ToolButton title="Upload image or PDF from your computer" onClick={() => fileInput.current?.click()} testid="kb-upload-file"><Upload className="h-4 w-4" /></ToolButton>
        <ToolButton title="Image by URL" onClick={() => { const url = window.prompt('Image URL'); if (url) insertHtml(`<img src="${url}" alt="" class="kb-image" />`); }} testid="kb-image"><ImageIcon className="h-4 w-4" /></ToolButton>
        <ToolButton
          title="Video embed"
          onClick={() => {
            const url = window.prompt('Video URL (YouTube, Vimeo or mp4)');
            if (!url) return;
            const embed = url.includes('youtu')
              ? `<div class="kb-embed"><iframe src="https://www.youtube.com/embed/${url.split(/v=|youtu\.be\//)[1]?.split(/[&?]/)[0]}" allowfullscreen title="video"></iframe></div>`
              : `<div class="kb-embed"><video src="${url}" controls></video></div>`;
            insertHtml(embed + '<p><br/></p>');
          }}
          testid="kb-video"
        ><Video className="h-4 w-4" /></ToolButton>
        <ToolButton title="PDF embed" onClick={() => { const url = window.prompt('PDF URL'); if (url) insertHtml(`<div class="kb-embed"><iframe src="${url}" title="pdf"></iframe></div><p><br/></p>`); }} testid="kb-pdf"><FileText className="h-4 w-4" /></ToolButton>
        <Divider />
        <ToolButton title="Info callout" onClick={() => insertHtml('<div class="kb-callout kb-callout-info"><strong>Note:</strong> Add detail here.</div><p><br/></p>')} testid="kb-callout-info"><Info className="h-4 w-4" /></ToolButton>
        <ToolButton title="Warning callout" onClick={() => insertHtml('<div class="kb-callout kb-callout-warning"><strong>Important:</strong> Add detail here.</div><p><br/></p>')} testid="kb-callout-warning"><AlertTriangle className="h-4 w-4" /></ToolButton>
        <ToolButton title="Success callout" onClick={() => insertHtml('<div class="kb-callout kb-callout-success"><strong>Tip:</strong> Add detail here.</div><p><br/></p>')} testid="kb-callout-success"><CheckCircle2 className="h-4 w-4" /></ToolButton>
        <ToolButton title="Expandable section" onClick={() => insertHtml('<details class="kb-expand"><summary>Click to expand</summary><p>Hidden detail</p></details><p><br/></p>')} testid="kb-expand"><ChevronRight className="h-4 w-4" /></ToolButton>
        <ToolButton title="Code block" onClick={() => insertHtml('<pre class="kb-code"><code>code</code></pre><p><br/></p>')} testid="kb-code"><Code className="h-4 w-4" /></ToolButton>
        <Divider />
        <ToolButton title="External link" onClick={() => { const url = window.prompt('Link URL'); if (url) exec('createLink', url); sync(); }} testid="kb-link"><Link2 className="h-4 w-4" /></ToolButton>
        <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onMouseDown={(e) => { e.preventDefault(); insertLinkToPage(); }} data-testid="kb-page-link">
          Link page
        </Button>
      </div>
      <div
        ref={ref}
        contentEditable
        role="textbox"
        aria-label="Page content"
        aria-multiline="true"
        suppressContentEditableWarning
        onInput={sync}
        onBlur={sync}
        onDragOver={(e) => { if (onUploadFile) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
        onDrop={(e) => { if (onUploadFile && e.dataTransfer.files?.length) { e.preventDefault(); uploadFiles(e.dataTransfer.files); } }}
        onPaste={(e) => {
          const files = Array.from(e.clipboardData?.files || []);
          if (onUploadFile && files.length) { e.preventDefault(); uploadFiles(files); }
        }}
        data-placeholder={placeholder}
        className={`kb-content overflow-y-auto p-5 sm:p-7 text-sm focus:outline-none [overflow-wrap:anywhere] ${fullHeight ? 'flex-1 min-h-0 bg-white' : 'min-h-[320px] max-h-[55vh]'}`}
        data-testid="kb-editor-body"
      />
      {onUploadFile && (
        <div className="shrink-0 flex flex-wrap gap-2 items-center justify-between px-4 py-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-500">
          <span data-testid="kb-drop-hint">
            {uploading ? 'Uploading...' : 'Drag & drop or paste a PDF, image or video anywhere above to embed it'}
          </span>
          <button type="button" onClick={() => fileInput.current?.click()} className="text-indigo-600 hover:underline" data-testid="kb-browse-files">
            Browse files
          </button>
        </div>
      )}
      <input
        ref={fileInput}
        type="file"
        multiple
        accept="image/*,application/pdf,video/mp4,video/webm,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt"
        className="hidden"
        onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }}
        data-testid="kb-file-input"
      />
    </div>
  );
};

export default RichTextEditor;
